import { useMemo } from 'react';
import { Order } from '@/model/Common.model';
import type { MeasurementComparatorProps, RenderFunctions } from './useMeasurementComparator.model';
import {
  filterMeasurementsToDate,
  findComparatorDateMeasurement,
  findDifference,
  findFirstMeasurement,
  findLatestMeasurement,
  findMaximumMeasurement,
  orderMeasurementsByDate,
} from './useMeasurementComparator.utils';
import type { Measurement } from '@/model/Measurement.model.ts';

const createComparator =
  (measurement: Measurement | null, key: string) =>
  (value: number | null): number | null =>
    findDifference(value, measurement?.variables?.[key] ?? null);

const useMeasurementComparator = ({
  calculations = [],
  measurements = [],
  trackingDate,
  diseaseInductionDate,
  measuredAtDate,
}: MeasurementComparatorProps) => {
  return useMemo(() => {
    const ascMeasurements = orderMeasurementsByDate(measurements, Order.asc);
    const measurementsToDate = filterMeasurementsToDate(ascMeasurements, measuredAtDate);

    return calculations.reduce<Record<string, RenderFunctions>>((acc, { id: calculationKey }) => {
      const firstMeasurement = findFirstMeasurement(measurementsToDate, calculationKey);
      const latestMeasurement = findLatestMeasurement(measurementsToDate, calculationKey);
      const maximumMeasurement = findMaximumMeasurement(measurementsToDate, calculationKey);
      const trackingDateMeasurement = findComparatorDateMeasurement(ascMeasurements, {
        comparatorDate: trackingDate,
        measurementKey: calculationKey,
      });
      const diseaseInductionMeasurement = findComparatorDateMeasurement(ascMeasurements, {
        comparatorDate: diseaseInductionDate,
        measurementKey: calculationKey,
      });

      acc[calculationKey] = {
        trackingDate: createComparator(trackingDateMeasurement, calculationKey),
        diseaseInductionDate: createComparator(diseaseInductionMeasurement, calculationKey),
        fromFirst: createComparator(firstMeasurement, calculationKey),
        fromLatest: createComparator(latestMeasurement, calculationKey),
        fromMaximum: createComparator(maximumMeasurement, calculationKey),
      };

      return acc;
    }, {});
  }, [calculations, measurements, trackingDate, diseaseInductionDate, measuredAtDate]);
};

export default useMeasurementComparator;
